import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Mail, Eye, Key, CheckCircle, XCircle } from 'lucide-react';
import { validateAccessCode } from '../utils/accessCodes';
import { logAccessCodeUsed, logAccessCodeFailed } from '../utils/analytics';

const ContactPromptModalWithAccess = ({ isOpen, onClose, onPreview }) => {
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  if (!isOpen) return null;

  const handleAccessCodeChange = (e) => {
    const value = e.target.value;
    setAccessCode(value);
    setError(''); // Clear error when user types
  };

  const handleAccessCodeSubmit = async (e) => {
    e.preventDefault();
    
    if (!accessCode.trim()) {
      setError('Please enter an access code');
      return;
    }

    setIsValidating(true);
    
    const trimmedCode = accessCode.trim();
    
    try {
      // Validate access code (checks expiration) - now async
      const validation = await validateAccessCode(trimmedCode);
      
      if (validation.valid) {
        // Track successful access code usage
        logAccessCodeUsed(validation.code, 'manual');
        
        // Store access code in localStorage
        try {
          localStorage.setItem('demo_access_code', validation.code);
          localStorage.setItem('demo_access_granted', 'true');
        } catch (error) {
          console.error('Failed to save access code to localStorage:', error);
          setError('Failed to save access. Please check your browser settings or try a different browser.');
          setIsValidating(false);
          return;
        }
        
        // Close modal and reload to apply access
        onClose();
        window.location.reload();
      } else if (validation.expired) {
        // Track expired access code attempt
        logAccessCodeFailed(trimmedCode);
        const expirationDate = new Date(validation.expirationDate).toLocaleDateString();
        setError(`This access code expired on ${expirationDate}. Please contact us for a new code.`);
        setIsValidating(false);
      } else {
        // Track failed access code attempt
        logAccessCodeFailed(trimmedCode);
        setError('Invalid access code. Please try again or contact us.');
        setIsValidating(false);
      }
    } catch (error) {
      console.error('Error validating access code:', error);
      setError('Error validating access code. Please try again.');
      setIsValidating(false);
    }
  };

  const handlePreview = () => {
    onPreview();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl">
        <div className="text-center">
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Thanks for your interest! 🌟
            </h2>
            <p className="text-gray-600">
              Enter your access code to try the demo, or contact us to request access.
            </p>
          </div>

          {/* Access Code Input */}
          <form onSubmit={handleAccessCodeSubmit} className="mb-6">
            <div className="mb-4">
              <label htmlFor="accessCode" className="block text-sm font-medium text-gray-700 mb-2 text-left">
                Access Code
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Key className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="accessCode"
                  value={accessCode}
                  onChange={handleAccessCodeChange}
                  placeholder="Enter your access code"
                  className={`block w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                    error ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isValidating}
                />
                {accessCode && !error && !isValidating && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <CheckCircle className="h-5 w-5 text-gray-400" />
                  </div>
                )}
                {error && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <XCircle className="h-5 w-5 text-red-500" />
                  </div>
                )}
              </div>
              {error && (
                <p className="mt-2 text-sm text-red-600 text-left">{error}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={isValidating || !accessCode.trim()}
              className="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isValidating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Validating...
                </>
              ) : (
                <>
                  <Key className="mr-2 h-5 w-5" />
                  Unlock Demo
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">or</span>
            </div>
          </div>

          {/* Buttons */}
          <div className="space-y-3">
            <Link
              to="/contact"
              onClick={onClose}
              className="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300"
            >
              <Mail className="mr-2 h-5 w-5" />
              Contact Us
            </Link>
            
            <button
              onClick={handlePreview}
              className="w-full inline-flex justify-center items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg shadow-sm text-gray-700 bg-white hover:bg-gray-50 transition-all duration-300"
            >
              <Eye className="mr-2 h-5 w-5" />
              Preview Demo
            </button>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 transition-colors duration-200"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactPromptModalWithAccess;

